import React, { useState, useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface CancelSubscriptionModalProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
  subscriptionPlanData?: any;
  currentPlanData?: any;
  billingPeriod?: "monthly" | "yearly";
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isVisible,
  onClose,
  onConfirm,
  isLoading = false,
  subscriptionPlanData,
  currentPlanData,
  billingPeriod = "monthly",
}) => {
  const { t } = useTranslation("settings");
  const { t: tPayment } = useTranslation("payment");
  const [step, setStep] = useState<1 | 2>(1);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  const handleClose = () => {
    setStep(1);
    onClose();
  };

  const handleContinueToCancel = () => {
    setStep(2);
  };

  const handleConfirmCancel = () => {
    onConfirm("User switched to free plan"); // Pass a default reason
  };

  if (!isVisible) return null;

  // Get current plan details - match by title and type since subscription_plan_id may not match plan.id
  const currentPlan = subscriptionPlanData?.plans?.find(
    (p: any) => p.title === currentPlanData?.title && p.type === currentPlanData?.type,
  );

  // Get free plan based on billing period
  const freePlan = subscriptionPlanData?.plans?.find(
    (p: any) => p.price === 0 && (billingPeriod === "monthly" ? p.type === 1 : p.type !== 1),
  );

  // Helper function to get translated feature label
  const getFeatureLabel = (key: string): string => {
    return tPayment(`subscriptionPlans.features.${key}`);
  };

  // Helper function to render feature value
  const renderFeatureValue = (key: string, value: any) => {
    if (typeof value === "boolean") {
      return <span className={value ? "feature-check" : "feature-cross"}>{value ? "✓" : "✗"}</span>;
    }

    if (typeof value === "string") {
      if (value == "-1") {
        if (key === "interview_recording") {
          return (
            <span className="feature-text" style={{ fontWeight: "bold" }}>
              0 {tPayment("subscriptionPlans.features.days")}
            </span>
          );
        }
        return <span className="feature-cross">✗</span>;
      }
    }

    if (typeof value === "number") {
      if (value == -1) {
        if (key === "interview_recording") {
          return (
            <span className="feature-text" style={{ fontWeight: "bold" }}>
              0 {tPayment("subscriptionPlans.features.days")}
            </span>
          );
        }
      }
      if (value === 0) {
        return (
          <span className="feature-text" style={{ color: "#28a745", fontWeight: "bold" }}>
            {tPayment("subscriptionPlans.features.unlimited")}
          </span>
        );
      }

      if (value >= 1) {
        const displayValue =
          key === "interview_recording"
            ? `${value.toLocaleString()} ${tPayment("subscriptionPlans.features.days")}`
            : value.toLocaleString();
        return (
          <span className="feature-text" style={{ fontWeight: "bold" }}>
            {displayValue}
          </span>
        );
      }
    }

    return <span className="feature-text">{String(value)}</span>;
  };

  // Helper function to render plan features
  const renderPlanFeatures = (plan: any) => {
    const featureOrder = [
      "active_jobs",
      "talent_scoutting",
      "live_video_interviews",
      "resume_parsing",
      "interview_recording",
      "ai_interview",
      "ai_candidate_searching",
      "meet_now",
      "candidate_search",
      "candidate_video_cv",
      "ai_screening",
      "interview_scheduling",
      "recruitment_analytics",
      "employer_branding",
      "ai_offer_letter",
      "support",
      "listing_duration",
    ];

    // Access features from nested features object or directly from plan (for backward compatibility)
    const features = plan.features || plan;

    return featureOrder
      .map((featureKey) => {
        const featureValue = features[featureKey];
        const featureLabel = getFeatureLabel(featureKey);

        if (featureValue === undefined || featureValue === null) {
          return null;
        }

        return (
          <li key={featureKey}>
            {renderFeatureValue(featureKey, featureValue)}
            <span className="feature-label" style={{ marginLeft: "8px" }}>
              {featureLabel}
            </span>
          </li>
        );
      })
      .filter(Boolean);
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="upgrade-plan-modal cancel-subscription-modal" onClick={(e) => e.stopPropagation()}>
        {step === 1 ? (
          // Step 1: Warning message
          <>
            <div className="modal-header">
              <div className="modal-header-content">
                <h2 className="modal-title">{t("billing.cancelSubscription.step1.title")}</h2>
              </div>
              <button className="modal-close-button" onClick={handleClose} disabled={isLoading}>
                ×
              </button>
            </div>

            <div className="modal-content cancel-subscription-step1">
              <div className="cancel-warning-content">
                <div className="cancel-warning-left">
                  <h3>{t("billing.cancelSubscription.step1.dataDeleteTitle")}</h3>
                  <div className="warning-list">
                    <p className="text-danger mb-2">
                      <i className="fa fa-times-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.dataItem1")}
                    </p>
                    <p className="text-danger mb-2">
                      <i className="fa fa-times-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.dataItem2")}
                    </p>
                    <p className="text-danger mb-2">
                      <i className="fa fa-times-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.dataItem3")}
                    </p>
                    <p className="text-danger mb-2">
                      <i className="fa fa-times-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.dataItem4")}
                    </p>
                    <p className="text-danger mb-2">
                      <i className="fa fa-times-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.dataItem5")}
                    </p>
                    <p className="text-danger mb-2">
                      <i className="fa fa-times-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.dataItem6")}
                    </p>
                    <p className="text-danger mb-2">
                      <i className="fa fa-times-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.dataItem7")}
                    </p>
                  </div>
                </div>

                <div className="cancel-warning-right">
                  <img
                    src="/images/plan-right-pic-n.jpg"
                    alt="Warning"
                    className="cancel-warning-image"
                    onError={(e) => {
                      // Fallback to a placeholder or remove image if not found
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              </div>
              <div className="available-warning-content">
                <div className="available-warning-left">
                  <h3>{t("billing.cancelSubscription.step1.dataDeleteTitle")}</h3>
                  <div className="warning-list">
                    <p className="text-success mb-2">
                      <i className="fa fa-check-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.featureItem1")}
                    </p>
                    <p className="text-success mb-2">
                      <i className="fa fa-check-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.featureItem2")}
                    </p>
                    <p className="text-success mb-2">
                      <i className="fa fa-check-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.featureItem3")}
                    </p>
                    <p className="text-success mb-2">
                      <i className="fa fa-check-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.featureItem4")}
                    </p>
                  </div>
                </div>

                <div className="available-warning-right">
                  <h3 className="mt-4">{t("billing.cancelSubscription.step1.featureLossTitle")}</h3>
                  <div className="warning-list">
                    <p className="text-success mb-2">
                      <i className="fa fa-check-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.featureItem5")}
                    </p>
                    <p className="text-success mb-2">
                      <i className="fa fa-check-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.featureItem6")}
                    </p>
                    <p className="text-success mb-2">
                      <i className="fa fa-check-circle me-2"></i>
                      {t("billing.cancelSubscription.step1.featureItem7")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="note-warning-content">
                <h3>{t("billing.cancelSubscription.step1.importantNoteTitle")}</h3>
                <p>{t("billing.cancelSubscription.step1.importantNote")}</p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-success stay-subscription-btn" onClick={handleClose} disabled={isLoading}>
                {t("billing.cancelSubscription.step1.stayButton")}
              </button>
              <button
                className="btn btn-outline-secondary continue-cancel-btn"
                onClick={handleContinueToCancel}
                disabled={isLoading}
              >
                {t("billing.cancelSubscription.step1.continueButton")}
              </button>
            </div>
          </>
        ) : (
          // Step 2: Compare plans
          <>
            <div className="modal-header">
              <div className="modal-header-content">
                <h2 className="modal-title">{t("billing.cancelSubscription.step2.title")}</h2>
              </div>
              <button className="modal-close-button" onClick={handleClose} disabled={isLoading}>
                ×
              </button>
            </div>

            <div className="modal-content cancel-subscription-step2">
              <div className="plan-comparison-container">
                {/* Current Plan */}
                {currentPlan && (
                  <div className="subscription-plan-card current-plan-card">
                    <div className="plan-header">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "12px",
                        }}
                      >
                        <h3 className="plan-name" style={{ margin: 0 }}>
                          {currentPlan.title}
                        </h3>
                        <div
                          className="plan-badge-current"
                          style={{ position: "static", transform: "none", left: "auto", top: "auto" }}
                        >
                          {t("billing.cancelSubscription.step2.currentPlan")}
                        </div>
                      </div>
                      {currentPlan?.type === 1 ? (
                        <div className="plan-price" style={{ textAlign: "left" }}>
                          <span className="price">${currentPlan.price}</span>
                          <span className="period">{tPayment("subscriptionPlans.pricing.perMonth")}</span>
                        </div>
                      ) : (
                        <>
                          <div className="plan-price" style={{ textAlign: "left" }}>
                            {currentPlan?.monthly_actual_price && currentPlan.price > 0 && (
                              <span
                                className="price-strikethrough"
                                style={{ textDecoration: "line-through", marginRight: "8px", opacity: 0.6 }}
                              >
                                ${currentPlan.monthly_actual_price}
                              </span>
                            )}
                            <span className="price">${Math.round(parseFloat(currentPlan.price) / 12)}</span>
                            <span className="period">{tPayment("subscriptionPlans.pricing.perMonth")}</span>
                          </div>
                          <div className="plan-price-yearly" style={{ textAlign: "left" }}>
                            {tPayment("subscriptionPlans.billedYearlyAt")}
                            <strong className="ms-1">${currentPlan.price}</strong>
                            <span className="period">{tPayment("subscriptionPlans.pricing.perYear")}</span>
                          </div>
                        </>
                      )}
                      <p className="plan-description">{currentPlan.description}</p>

                      <div className="plan-action">
                        <button className="plan-button current-plan-button" onClick={handleClose}>
                          {t("billing.cancelSubscription.step2.stayButton")}
                        </button>
                      </div>
                    </div>

                    <div className="plan-features">
                      <ul style={{ listStyle: "none", padding: 0 }}>{renderPlanFeatures(currentPlan)}</ul>
                    </div>
                  </div>
                )}

                {/* Free Plan */}
                {freePlan && (
                  <div className="subscription-plan-card free-plan-card">
                    {/* <div className="plan-badge-free">{tPayment("subscriptionPlans.pricing.free")}</div> */}

                    <div className="plan-header">
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                        <h3 className="plan-name" style={{ margin: 0 }}>
                          {freePlan.title}
                        </h3>
                      </div>
                      <div className="plan-price" style={{ textAlign: "left" }}>
                        <span className="price">{tPayment("subscriptionPlans.pricing.free")}</span>
                      </div>
                      <p className="plan-description">{t("billing.cancelSubscription.step2.noSubscription")}</p>
                      <div className="plan-action">
                        <button
                          className="plan-button switch-free-button"
                          onClick={handleConfirmCancel}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <i className="fa fa-spinner fa-spin me-2"></i>
                              {t("billing.cancelSubscription.step2.processing")}
                            </>
                          ) : (
                            t("billing.cancelSubscription.step2.switchButton")
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="plan-features">
                      <ul style={{ listStyle: "none", padding: 0 }}>{renderPlanFeatures(freePlan)}</ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
